/**
 * Ephemeral worklist state (GDN-3196): the page-cursor stack and scroll
 * position, in sessionStorage keyed by the canonical-state signature.
 *
 * Deliberately NOT in the URL: cursors are server-opaque tokens with a
 * server-side lifetime — a shared or reloaded link must not depend on them.
 * Restore is best-effort: a signature mismatch or malformed record reads as
 * "nothing stored", and a stale cursor lands the biller on page 1 of the
 * correctly-filtered list (ClaimsTab owns that fallback).
 *
 * Single-slot model: only the most recently used list state keeps ephemera,
 * so storage stays bounded and stale records can't accumulate.
 */

export interface ClaimWorkEphemera {
  signature: string;
  cursor?: string;
  cursorHistory: Array<string | undefined>;
  scrollY?: number;
}

const STORAGE_KEY = 'jotbill-claim-work-ephemera';

function readRaw(): ClaimWorkEphemera | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ClaimWorkEphemera>;
    if (typeof parsed.signature !== 'string') return null;
    return {
      signature: parsed.signature,
      cursor: typeof parsed.cursor === 'string' ? parsed.cursor : undefined,
      /* JSON round-trips `undefined` entries (page 1's no-cursor) as null —
         MAP them back rather than filtering, which would shift positions and
         corrupt the stack's page numbering. */
      cursorHistory: Array.isArray(parsed.cursorHistory)
        ? parsed.cursorHistory.map(entry => (typeof entry === 'string' ? entry : undefined))
        : [],
      scrollY: typeof parsed.scrollY === 'number' ? parsed.scrollY : undefined,
    };
  } catch {
    return null;
  }
}

/** The stored record, only when it belongs to THIS canonical state. */
export function readClaimWorkEphemera(signature: string): ClaimWorkEphemera | null {
  const stored = readRaw();
  return stored && stored.signature === signature ? stored : null;
}

export function writeClaimWorkEphemera(ephemera: ClaimWorkEphemera): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ephemera));
  } catch {
    // Storage unavailable/full — ephemera are best-effort by definition.
  }
}

/** Leave-time scroll capture: merge into the stored record if one exists. */
export function updateClaimWorkEphemeraScroll(scrollY: number): void {
  const stored = readRaw();
  if (!stored) return;
  writeClaimWorkEphemera({ ...stored, scrollY });
}
